import {
	useEffect,
	useLayoutEffect,
	useCallback,
	useImperativeHandle,
	useRef,
	type Dispatch,
	type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import NoImageIcon from 'assets/icons/no-image.svg';
import RefreshIcon from 'assets/icons/refresh.svg';
import useIIIFViewer from '../../hooks/use-iiif-viewer';
import { ReadingToolsPanels } from '../reading-tools-panels';
import { HotSpot } from '../hotspot';
import styles from './viewer.module.scss';
import type { PortableTextBlock } from '@portabletext/types';
import type { DigitizedPrimarySourcePage } from '../../types';
import type { ViewerAction } from '../../types';

export default function Viewer({
	currentPageIndex,
	transcription,
	translation,
	showTranscription,
	showTranslation,
	showMobileInfoPanel,
	showComments,
	dispatch,
	hasError,
	sources,
	comments,
	isMobile,
	activeCommentId,
	ref,
}: ViewerProps) {
	const statusPaneRef = useRef<HTMLDivElement>(null);
	const desktopReadingToolsRef = useRef<HTMLDivElement>(null);

	const canvasPressHandler = useCallback(
		({ target }: PointerEvent) => {
			if (!showComments || showMobileInfoPanel) return;
			if (!(target instanceof Element)) return;

			const overlayEl = target.closest('[data-overlay-id]');
			const overlayId = overlayEl?.getAttribute('data-overlay-id') ?? null;

			if (isMobile) {
				if (!overlayId) return;

				dispatch({ type: 'show_comment', commentId: overlayId });
			}

			dispatch(
				overlayId
					? { type: 'show_comment', commentId: overlayId }
					: { type: 'hide_shown_comment' },
			);
		},
		[dispatch, isMobile, showComments, showMobileInfoPanel],
	);
	const canvasKeyHandler = useCallback(
		({ key, target }: KeyboardEvent) => {
			if (key === 'Escape') {
				dispatch({ type: 'hide_shown_comment' });
			} else if (key === 'Enter' || key === ' ') {
				if (!(target instanceof Element)) return;

				const overlayEl = target.closest('[data-overlay-id]');
				const overlayId = overlayEl?.getAttribute('data-overlay-id') ?? null;

				if (!overlayId) return;

				dispatch(
					overlayId === activeCommentId
						? { type: 'hide_shown_comment' }
						: { type: 'show_comment', commentId: overlayId },
				);
			}
		},
		[activeCommentId, dispatch],
	);

	const {
		setContainerEl: setViewerContainerEl,
		containerEl: viewerContainerEl,
		isReady: isIIIFViewerReady,
		isLoading: isIIIFViewerLoading,
		hasError: hasIIIFViewerError,
		canZoomIn,
		canZoomOut,
		zoomIn,
		zoomOut,
		setMargins,
		reload,
		overlayHandles,
	} = useIIIFViewer({
		index: currentPageIndex,
		sources,
		canvasPressHandler,
		canvasKeyHandler,
	});

	// Synchronize IIIFViewer state with app state.
	useEffect(() => {
		dispatch({ type: 'set_can_zoom_in', canZoomIn });
	}, [dispatch, canZoomIn]);
	useEffect(() => {
		dispatch({ type: 'set_can_zoom_out', canZoomOut });
	}, [dispatch, canZoomOut]);
	useEffect(() => {
		dispatch({
			type: isIIIFViewerLoading ? 'start_loading' : 'stop_loading',
		});
	}, [dispatch, isIIIFViewerLoading]);
	useEffect(() => {
		dispatch({ type: hasIIIFViewerError ? 'set_has_error' : 'clear_error' });
	}, [dispatch, hasIIIFViewerError]);

	// We need to bind two separate click (well, "mousedown") handlers for the viewer and its surrounding
	// UI since the OpenSeadragon viewer captures all clicks within its container.
	useEffect(() => {
		const handleMousedown = ({ target }: MouseEvent) => {
			if (
				!isIIIFViewerReady ||
				showMobileInfoPanel ||
				(target as Node)?.contains(viewerContainerEl)
			) {
				return;
			}

			dispatch({ type: 'hide_shown_comment' });
		};
		document.addEventListener('mousedown', handleMousedown);
		return () => document.removeEventListener('mousedown', handleMousedown);
	}, [dispatch, isIIIFViewerReady, viewerContainerEl, showMobileInfoPanel]);

	// Update viewer visibility based on reading tools / mobile info panel state.
	const handleViewportChange = useCallback(() => {
		if (isMobile && showMobileInfoPanel) {
			const marginBottom =
				(viewerContainerEl?.getBoundingClientRect().height ?? 0) * 0.5;

			if (hasError) {
				statusPaneRef.current?.style.setProperty('margin-right', '0');
				statusPaneRef.current?.style.setProperty(
					'margin-bottom',
					`${marginBottom}px`,
				);
			} else {
				setMargins({
					right: 0,
					bottom: marginBottom,
				});
			}

			return;
		}

		if (!isMobile && (showTranslation || showTranscription)) {
			const marginRight =
				desktopReadingToolsRef.current?.getBoundingClientRect().width ?? 0;

			if (hasError) {
				statusPaneRef.current?.style.setProperty(
					'margin-right',
					`${marginRight}px`,
				);
				statusPaneRef.current?.style.setProperty('margin-bottom', '0');
			} else {
				setMargins({
					right: marginRight,
					bottom: 0,
				});
			}

			return;
		}

		if (hasError) {
			statusPaneRef.current?.style.setProperty('margin-right', '0');
			statusPaneRef.current?.style.setProperty('margin-bottom', '0');
		} else {
			setMargins({
				right: 0,
				bottom: 0,
			});
		}
	}, [
		isMobile,
		setMargins,
		showMobileInfoPanel,
		showTranslation,
		showTranscription,
		viewerContainerEl,
		desktopReadingToolsRef,
		hasError,
	]);
	useLayoutEffect(() => {
		if (isMobile) return;

		handleViewportChange();
	}, [
		isMobile,
		showTranslation,
		showTranscription,
		currentPageIndex,
		hasError,
		handleViewportChange,
	]);
	useLayoutEffect(() => {
		if (!isMobile) return;

		handleViewportChange();
	}, [
		isMobile,
		showMobileInfoPanel,
		currentPageIndex,
		hasError,
		handleViewportChange,
	]);
	useEffect(() => {
		if (!viewerContainerEl) return;

		window.addEventListener('resize', handleViewportChange);

		return () => {
			window.removeEventListener('resize', handleViewportChange);
		};
	}, [handleViewportChange, viewerContainerEl]);

	// Expose viewer controls to parent via ref.
	useImperativeHandle(
		ref,
		() => ({
			zoomIn,
			zoomOut,
			setMargins,
			reload,
		}),
		[zoomIn, zoomOut, setMargins, reload],
	);

	return (
		<div className={styles.container}>
			<div className={styles.viewerContainer} ref={setViewerContainerEl}>
				{hasError && (
					<div className={styles.statusPane} ref={statusPaneRef}>
						<div className={styles.errorMessageWrapper}>
							<NoImageIcon className={styles.noImageIcon} />
							<div className={styles.errorMessage}>
								Sorry, we couldn&apos;t load this image.
							</div>
							<div
								role="button"
								className={styles.retryButton}
								onClick={reload}
							>
								<RefreshIcon />
								Retry
							</div>
						</div>
					</div>
				)}
			</div>
			{showComments &&
				overlayHandles.map(({ id, el }) => {
					const { content, citation } = comments[id];
					return createPortal(
						<HotSpot
							key={id}
							id={id}
							content={content}
							citation={citation}
							containerEl={el}
							popoverBoundaryElement={viewerContainerEl}
							isMobile={isMobile}
							isActiveComment={id === activeCommentId}
						/>,
						el,
					);
				})}
			{!isMobile && (
				<div
					className={styles.readingToolsPanelsWrapper}
					ref={desktopReadingToolsRef}
				>
					<ReadingToolsPanels
						transcription={transcription}
						translation={translation}
						dispatch={dispatch}
						showTranscription={showTranscription}
						showTranslation={showTranslation}
					/>
				</div>
			)}
		</div>
	);
}

interface ViewerProps {
	sources: DigitizedPrimarySourcePage[];
	hasError: boolean;
	isLoading: boolean;
	currentPageIndex: number;
	showTranscription: boolean;
	showTranslation: boolean;
	showMobileInfoPanel: boolean;
	translation?: PortableTextBlock[];
	transcription?: PortableTextBlock[];
	isMobile: boolean;
	showComments: boolean;
	activeCommentId: string | null;
	comments: Record<
		string,
		{
			x: number;
			y: number;
			content: PortableTextBlock[];
			citation: PortableTextBlock[];
		}
	>;
	dispatch: Dispatch<ViewerAction>;
	ref: Ref<{
		zoomIn: () => void;
		zoomOut: () => void;
		setMargins: (margins: {
			top?: number;
			right?: number;
			bottom?: number;
			left?: number;
		}) => void;
		reload: () => void;
	}>;
}
